import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  CalendarEvent,
  CalendarEventType,
  CalendarVisibility,
  useUpdateCalendarEvent,
  useDeleteCalendarEvent,
} from "@/hooks/useCalendarEvents";
import { useProjects } from "@/hooks/useProjects";
import { toast } from "sonner";

interface Props {
  event: CalendarEvent | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

const EVENT_TYPES: { value: CalendarEventType; label: string }[] = [
  { value: "milestone", label: "Milestone" },
  { value: "wo_schedule", label: "Work Order" },
  { value: "draw_deadline", label: "Draw Deadline" },
  { value: "inspection", label: "Inspection" },
  { value: "permit", label: "Permit" },
  { value: "meeting", label: "Meeting" },
  { value: "personal", label: "Personal" },
];

export const EditEventDialog = ({ event, open, onOpenChange }: Props) => {
  const [title, setTitle] = useState("");
  const [eventType, setEventType] = useState<CalendarEventType>("milestone");
  const [projectId, setProjectId] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [visibility, setVisibility] = useState<CalendarVisibility>("team");
  const [description, setDescription] = useState("");

  const { data: projects } = useProjects();
  const updateEvent = useUpdateCalendarEvent();
  const deleteEvent = useDeleteCalendarEvent();

  const handleDelete = async () => {
    if (!event) return;
    if (!confirm(`Delete "${event.title}"? This cannot be undone.`)) return;
    try {
      await deleteEvent.mutateAsync(event.id);
      toast.success("Event deleted");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to delete event");
    }
  };

  useEffect(() => {
    if (event && open) {
      setTitle(event.title);
      setEventType(event.event_type);
      setProjectId(event.project_id || "");
      setStartDate(new Date(event.start_date + "T00:00:00"));
      setEndDate(event.end_date && event.end_date !== event.start_date ? new Date(event.end_date + "T00:00:00") : undefined);
      setAllDay(event.all_day);
      setStartTime(event.start_time?.slice(0, 5) || "");
      setEndTime(event.end_time?.slice(0, 5) || "");
      setVisibility(event.visibility);
      setDescription(event.description || "");
    }
  }, [event, open]);

  const handleSave = () => {
    if (!event || !title.trim() || !startDate) {
      toast.error("Title and start date are required");
      return;
    }
    updateEvent.mutate(
      {
        id: event.id,
        title: title.trim(),
        event_type: eventType,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: endDate ? format(endDate, "yyyy-MM-dd") : format(startDate, "yyyy-MM-dd"),
        start_time: !allDay && startTime ? startTime + ":00" : null,
        end_time: !allDay && endTime ? endTime + ":00" : null,
        all_day: allDay,
        visibility,
        description: description.trim() || null,
        project_id: projectId || null,
      },
      {
        onSuccess: () => {
          toast.success("Event updated");
          onOpenChange(false);
        },
        onError: (e) => toast.error(e.message),
      }
    );
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={eventType} onValueChange={(v) => setEventType(v as CalendarEventType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {(projects || []).map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "MMM d, yyyy") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "MMM d, yyyy") : "Same day"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={allDay} onCheckedChange={(c) => setAllDay(!!c)} />
              All day
            </label>
            {!allDay && (
              <div className="flex items-center gap-2">
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-28" />
                <span className="text-muted-foreground text-sm">to</span>
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-28" />
              </div>
            )}
          </div>

          <div>
            <Label>Visibility</Label>
            <RadioGroup value={visibility} onValueChange={(v) => setVisibility(v as CalendarVisibility)} className="flex gap-4 mt-1">
              {(["team", "personal", "project", "company"] as const).map((v) => (
                <label key={v} className="flex items-center gap-1.5 text-sm capitalize cursor-pointer">
                  <RadioGroupItem value={v} />
                  {v}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional notes…" rows={2} />
          </div>

          <div className="flex justify-between gap-2 pt-2">
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive gap-1"
              onClick={handleDelete}
              disabled={deleteEvent.isPending || updateEvent.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateEvent.isPending}>
                {updateEvent.isPending ? "Saving…" : "Update Event →"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
